import { useForm } from '@inertiajs/react';
import { useState, useEffect } from 'react';

export default function Form({ product, onSubmit, submitText }) {
  const [imagePreviews, setImagePreviews] = useState([]);

  const form = useForm({
    name: product.name || '',
    description: product.description || '',
    images: [],
    deleted_image_ids: [],
    quantity: product.quantity || '',
    price: product.price || '',
  });

  const { data, setData, errors, processing } = form;

  useEffect(() => {
    if (product && product.image_urls && product.image_urls.length > 0) {
      const existingPreviews = product.image_urls.map((image) => ({
        url: image.url,
        name: image.filename || 'existing_image',
        id: image.id,
        isExisting: true,
      }));
      setImagePreviews(existingPreviews);
    }
  }, [product]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = [];
    const newImages = [...data.images]; // Keep previously selected NEW images

    files.forEach((file) => {
      newImages.push(file);
      newPreviews.push({
        url: URL.createObjectURL(file), // Create a temporary URL for preview
        name: file.name,
        file: file,
        isExisting: false, // Mark as a new image
      });
    });

    setData('images', newImages);
    setImagePreviews((prev) => [...prev, ...newPreviews]);

    // Clear the input field to allow selecting the same file(s) again if needed
    e.target.value = null;
  };

  const removeImage = (indexToRemove) => {
    const imageToRemove = imagePreviews[indexToRemove];

    if (imageToRemove.isExisting && imageToRemove.id) {
      setData('deleted_image_ids', [
        ...data.deleted_image_ids,
        imageToRemove.id,
      ]);
    } else {
      const updatedImages = data.images.filter((_, idx) => {
        return imagePreviews.findIndex((p) => p.file === _) !== indexToRemove;
      });
      setData('images', updatedImages);
      URL.revokeObjectURL(imageToRemove.url);
    }

    // Remove from previews regardless of whether it's new or existing
    const updatedPreviews = imagePreviews.filter(
      (_, index) => index !== indexToRemove
    );
    setImagePreviews(updatedPreviews);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className='contents'>
      <div className='my-5'>
        <label htmlFor='name' className='font-body font-bold'>
          Name
        </label>
        <input
          type='text'
          name='name'
          id='name'
          placeholder='Enter product name'
          value={data.name}
          className='block font-bold bg-transparent shadow rounded-md outline-yellow-secondary px-3 py-2 mt-2 w-full'
          onChange={(e) => setData('name', e.target.value)}
        />
        {errors.name && (
          <div className='text-red-500 px-3 py-2 font-medium'>
            {errors.name}
          </div>
        )}
      </div>

      <div className='my-5'>
        <label htmlFor='images'>Images</label>
        <input
          type='file'
          name='images[]'
          accept='image/*'
          id='images'
          multiple
          className='block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full'
          onChange={handleImageChange}
        />
        {errors.images && (
          <div className='text-red-500 px-3 py-2 font-medium'>
            {errors.images}
          </div>
        )}

        {/* Image Previews */}
        {imagePreviews.length > 0 && (
          <div className='mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'>
            {imagePreviews.map((preview, index) => (
              <div key={index} className='relative group'>
                <img
                  src={preview.url}
                  alt={preview.name}
                  className='w-full h-32 object-cover rounded-md shadow-md'
                />
                <button
                  type='button'
                  onClick={() => removeImage(index)}
                  className='absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200'
                  aria-label={`Remove ${preview.name}`}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
                <p className='text-sm truncate mt-1'>{preview.name}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='my-5'>
        <label htmlFor='quantity' className='font-body font-bold'>
          Quantity
        </label>
        <input
          type='number'
          name='quantity'
          id='quantity'
          placeholder='Enter the product quantity'
          value={data.quantity}
          className='block font-bold bg-transparent shadow rounded-md outline-yellow-secondary px-3 py-2 mt-2 w-full'
          onChange={(e) => setData('quantity', e.target.value)}
        />
        {errors.quantity && (
          <div className='text-red-500 px-3 py-2 font-medium'>
            {errors.quantity}
          </div>
        )}
      </div>

      <div className='my-5'>
        <label htmlFor='price' className='font-body font-bold'>Price</label>
        <input
          type='number'
          name='price'
          id='price'
          placeholder='Enter the selling price GHS...'
          value={data.price}
          className='block font-bold bg-transparent shadow rounded-md outline-yellow-secondary px-3 py-2 mt-2 w-full'
          onChange={(e) => setData('price', e.target.value)}
        />
        {errors.price && (
          <div className='text-red-500 px-3 py-2 font-medium'>
            {errors.price}
          </div>
        )}
      </div>

      <div className='my-5'>
        <label htmlFor='description' className='font-body font-bold'>Description</label>
        <textarea
          name='description'
          id='description'
          value={data.description}
          placeholder='Provide the product description here ...'
          className='block font-bold bg-transparent shadow rounded-md outline-yellow-secondary px-3 py-2 mt-2 w-full h-32 resize-y'
          onChange={(e) => setData('description', e.target.value)}
        ></textarea>
        {errors.description && (
          <div className='text-red-500 px-3 py-2 font-medium'>
            {errors.description}
          </div>
        )}
      </div>

      <div className='inline'>
        <button type='submit' disabled={processing} className='flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white font-body font-bold py-3 px-6 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1'>
          {submitText}
        </button>
      </div>
    </form>
  );
}
